import { flushSync } from 'react-dom';
import {
  ActivityType,
  EmailTemplateType,
  PlacementTask,
  PlacementType,
  SubmissionEmailLabel,
  SubmissionStatus,
} from 'enums';
import { useCompletePlacementTask } from 'hooks';
import { logger } from 'utils';
import { PostEmailInformation } from 'broker/components/EmailEditor/types';
import SubmissionEmailEditor from 'broker/pages/SubmissionWorkspacePage/components/SubmissionEmailEditor';
import useSubmissionsWorkspace from 'broker/pages/SubmissionWorkspacePage/store/useSubmissionWorkspace';
import EmailStepContent from './EmailStepContent';
import { StepProps } from './types';
import { useGetProposalEmailRecipients } from './useGetProposalEmailRecipients';

export default function EmailStep(props: StepProps) {
  const { submission, setIsDirty } = props;

  const { updateSubmission, logSubmissionActivity } = useSubmissionsWorkspace();
  const { completePlacementTask } = useCompletePlacementTask();

  const proposalTemplate =
    submission.placementType === PlacementType.Direct
      ? EmailTemplateType.RenewalProposal
      : EmailTemplateType.NewBusinessProposal;

  const { preSelectedRecipients, ccEmailsInit, bccEmailsInit } = useGetProposalEmailRecipients(submission);

  return (
    <SubmissionEmailEditor
      isDirty={false}
      emailEditorProps={{
        preSelectedTemplateType: !submission.proposal?.body ? proposalTemplate : undefined,
        preSelectedRecipients,
        ccEmailsInit,
        bccEmailsInit,
        emailLabel: SubmissionEmailLabel.Proposal,
        repliedThreadBody: submission.proposal?.body,
        repliedThreadSubject: submission.proposal?.subject ? submission.proposal?.subject : undefined,
        preSelectedFileIds: submission.proposal?.attachedFiles,
        postEmailsSent: async (postEmailInformation?: PostEmailInformation) => {
          try {
            // Clear the draft ack email body and selected products after sending the email
            await updateSubmission(submission!.id, {
              proposal: null,
              status: SubmissionStatus.ProposalSent,
            });

            logSubmissionActivity({
              activityType: ActivityType.ProposalSent,
            });

            const emailMessageId = postEmailInformation?.emailMessageIds?.[0];

            await completePlacementTask(submission, PlacementTask.SendProposal, {
              ...(emailMessageId && { metadata: { emailMessageId } }),
            });

            flushSync(() => {
              setIsDirty(false);
            });
          } catch (error) {
            logger.log('error', error as Error);
          }
        },
      }}
    >
      <EmailStepContent {...props} />
    </SubmissionEmailEditor>
  );
}
